import * as ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AnalysisResults, AxisData } from '../types';

export interface ExportOptions {
  includeCharts: boolean;
  includePercentages: boolean;
  includeDetailedAnalysis: boolean;
  chartTypes: ('column' | 'pie' | 'line' | 'radar')[];
  colorScheme: 'blue' | 'green' | 'purple' | 'professional';
  workshopName?: string; // اسم الورشة المحددة للتصدير
}

export interface ExportMetrics {
  totalQuestions: number;
  totalWorkshops: number;
  completionRate: number;
  reliabilityIndex: number;
  improvementAreas: string[];
  strongPoints: string[];
}

export class AdvancedExporter {
  private workbook: ExcelJS.Workbook;
  private colorSchemes = {
    blue: ['#1E88E5', '#42A5F5', '#90CAF9', '#E3F2FD'],
    green: ['#43A047', '#66BB6A', '#A5D6A7', '#E8F5E9'],
    purple: ['#8E24AA', '#AB47BC', '#CE93D8', '#F3E5F5'],
    professional: ['#2C3E50', '#34495E', '#7F8C8D', '#ECF0F1']
  };

  constructor() {
    this.workbook = new ExcelJS.Workbook();
    this.workbook.creator = 'Survey Analyzer Pro';
    this.workbook.lastModifiedBy = 'Survey Analyzer Pro';
    this.workbook.created = new Date();
    this.workbook.modified = new Date();
  }

  // دالة لتنظيف أسماء أوراق العمل من الرموز المحظورة
  private sanitizeWorksheetName(name: string): string {
    // إزالة الرموز المحظورة في Excel: * ? : \ / [ ]
    let cleanName = name.replace(/[*?:\\\/\[\]]/g, '');
    
    // إزالة المسافات الزائدة وقطع النص إذا كان طويلاً جداً
    cleanName = cleanName.trim();
    
    // Excel لديه حد أقصى 31 حرف لاسم ورقة العمل
    if (cleanName.length > 31) {
      cleanName = cleanName.substring(0, 28) + '...';
    }
    
    // إذا كان الاسم فارغاً بعد التنظيف، استخدم اسم افتراضي
    if (cleanName.length === 0) {
      cleanName = 'ورقة عمل';
    }
    
    return cleanName;
  }

  async generateAdvancedExcel(
    analysisResults: AnalysisResults,
    axes: Record<string, string[]>,
    options: ExportOptions
  ): Promise<Buffer> {
    if (options.workshopName) {
      // تصدير خاص بورشة واحدة
      await this.createWorkshopSpecificReport(analysisResults, axes, options);
    } else {
      // تصدير شامل لجميع الورش (الوضع القديم)
      await this.createExecutiveSummary(analysisResults, options);
      await this.createDetailedAnalysis(analysisResults, axes, options);
      await this.createComparisonSheet(analysisResults, options);
      await this.createChartsSheet(analysisResults, options);
      await this.createRecommendationsSheet(analysisResults);
    }
    
    // تطبيق التنسيق المهني
    this.applyProfessionalFormatting(options.colorScheme);
    
    return await this.workbook.xlsx.writeBuffer() as Buffer;
  }

  private async createExecutiveSummary(
    analysisResults: AnalysisResults,
    options: ExportOptions
  ) {
    const worksheet = this.workbook.addWorksheet('الملخص التنفيذي');

    // العنوان الرئيسي
    worksheet.mergeCells('A1:H3');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'تقرير تحليل ورش العمل التدريبية - الملخص التنفيذي';
    titleCell.font = { bold: true, size: 18, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // المؤشرات الرئيسية
    let row = 5;
    const kpis = this.calculateKPIs(analysisResults);
    
    worksheet.addRow(['المؤشرات الرئيسية']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const kpiData = [
      ['المتوسط العام', analysisResults.overall.average.toFixed(2), this.calculatePercentage(analysisResults.overall.average, 5)],
      ['إجمالي الاستجابات', (analysisResults.overall.total_responses !== undefined && analysisResults.overall.total_responses !== null) ? analysisResults.overall.total_responses.toString() : '0', '100%'],
      ['عدد ورش العمل', kpis.totalWorkshops.toString(), '100%'],
      ['إجمالي الأسئلة', kpis.totalQuestions.toString(), '100%'],
      ['معدل الاكتمال', `${kpis.completionRate.toFixed(1)}%`, this.getCompletionStatus(kpis.completionRate)],
      ['مؤشر الموثوقية', kpis.reliabilityIndex.toFixed(3), this.getReliabilityStatus(kpis.reliabilityIndex)]
    ];

    worksheet.addRow(['المؤشر', 'القيمة', 'النسبة المئوية / الحالة']);
    for (const kpi of kpiData) {
      worksheet.addRow(kpi);
    }

    // تنسيق جدول المؤشرات
    this.formatTable(worksheet, row, row + kpiData.length + 1, 3, options.colorScheme);

    // تحليل المحاور
    row += kpiData.length + 4;
    worksheet.addRow(['تحليل المحاور']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const axisHeaders = ['المحور', 'المتوسط', 'النسبة المئوية', 'مستوى الأداء', 'الترتيب'];
    worksheet.addRow(axisHeaders);
    
    const sortedAxes = Object.entries(analysisResults.by_axis)
      .sort(([,a], [,b]) => b.average - a.average)
      .map(([name, data], index) => [
        name,
        data.average.toFixed(2),
        this.calculatePercentage(data.average, 5),
        data.performance_level,
        (index + 1).toString()
      ]);

    for (const axisData of sortedAxes) {
      worksheet.addRow(axisData);
    }

    this.formatTable(worksheet, row, row + sortedAxes.length + 1, 5, options.colorScheme);
  }

  private async createDetailedAnalysis(
    analysisResults: AnalysisResults,
    axes: Record<string, string[]>,
    options: ExportOptions
  ) {
    for (const [axisName, axisData] of Object.entries(analysisResults.by_axis)) {
      const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`تفاصيل - ${axisName}`));

      // عنوان المحور
      worksheet.mergeCells('A1:F2');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `تحليل مفصل - محور ${axisName}`;
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '34495E' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

      // ملخص المحور
      let row = 4;
              const summaryData = [
          ['المتوسط العام', axisData.average.toFixed(2), this.calculatePercentage(axisData.average, 5)],
        ['مستوى الأداء', axisData.performance_level, this.getPerformanceColor(axisData.performance_level)],
        ['عدد الأسئلة', Object.keys(axisData.question_averages).length.toString(), '100%']
      ];

      worksheet.addRow(['الخاصية', 'القيمة', 'النسبة المئوية / الحالة']);
      for (const summary of summaryData) {
        worksheet.addRow(summary);
      }

      this.formatTable(worksheet, row, row + summaryData.length + 1, 3, options.colorScheme);

      // تفاصيل الأسئلة
      row += summaryData.length + 4;
      worksheet.addRow(['تفاصيل الأسئلة']);
      worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
      row++;

      const questionHeaders = ['السؤال', 'المتوسط', 'النسبة المئوية', 'التقييم', 'نقاط التحسين'];
      worksheet.addRow(questionHeaders);

              const sortedQuestions = Object.entries(axisData.question_averages)
          .sort(([,a], [,b]) => b - a)
          .map(([question, average]) => [
            question,
            average.toFixed(2),
            this.calculatePercentage(average, 5),
            this.getPerformanceLevel(average),
            this.generateRecommendation(average)
          ]);

      for (const questionData of sortedQuestions) {
        worksheet.addRow(questionData);
      }

      this.formatTable(worksheet, row, row + sortedQuestions.length + 1, 5, options.colorScheme);

      // إضافة الرسوم البيانية إذا كانت مفعلة
      if (options.includeCharts) {
        await this.addChartsToWorksheet(worksheet, axisData, options);
      }
    }
  }

  private async createComparisonSheet(
    analysisResults: AnalysisResults,
    options: ExportOptions
  ) {
    const worksheet = this.workbook.addWorksheet('مقارنة ورش العمل');

    // العنوان
    worksheet.mergeCells('A1:H2');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'مقارنة شاملة بين ورش العمل';
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // جدول المقارنة
    let row = 4;
    const headers = ['ورشة العمل', 'عدد الاستجابات', 'المتوسط العام', 'النسبة المئوية', 'مستوى الأداء', 'الترتيب', 'نقاط القوة', 'مجالات التحسين'];
    worksheet.addRow(headers);

    // تشخيص بيانات الورش قبل التصدير
    console.log('🔍 تشخيص جميع الورش قبل التصدير:');
    Object.entries(analysisResults.by_workshop).forEach(([workshop, data]) => {
      console.log(`📊 الورشة "${workshop}":`, {
        response_count: data.response_count,
        response_count_type: typeof data.response_count,
        overall_average: data.overall_average
      });
    });

    const sortedWorkshops = Object.entries(analysisResults.by_workshop)
      .sort(([,a], [,b]) => b.overall_average - a.overall_average)
      .map(([workshop, data], index) => {
        const responseCountValue = (data.response_count !== undefined && data.response_count !== null) ? data.response_count.toString() : '0';
        console.log(`📝 تصدير الورشة "${workshop}": response_count = ${data.response_count} -> "${responseCountValue}"`);
        
        return [
          workshop,
          responseCountValue,
          data.overall_average.toFixed(2),
          this.calculatePercentage(data.overall_average, 5),
          data.performance_level,
          (index + 1).toString(),
          this.identifyStrengths(data),
          this.identifyImprovementAreas(data)
        ];
      });

    for (const workshopData of sortedWorkshops) {
      worksheet.addRow(workshopData);
    }

    this.formatTable(worksheet, row, row + sortedWorkshops.length + 1, 8, options.colorScheme);

    // إحصائيات متقدمة
    row += sortedWorkshops.length + 4;
    await this.addAdvancedStatistics(worksheet, analysisResults, row, options);
  }

  private async createChartsSheet(
    analysisResults: AnalysisResults,
    options: ExportOptions
  ) {
    if (!options.includeCharts) return;

    const worksheet = this.workbook.addWorksheet('الرسوم البيانية');

    // إنشاء رسوم بيانية مختلفة حسب الخيارات المحددة
    let row = 1;
    
    for (const chartType of options.chartTypes) {
      await this.createChart(worksheet, analysisResults, chartType, row, options);
      row += 20; // ترك مسافة بين الرسوم البيانية
    }
  }

  private async createRecommendationsSheet(analysisResults: AnalysisResults) {
    const worksheet = this.workbook.addWorksheet('التوصيات والإجراءات');

    // العنوان
    worksheet.mergeCells('A1:F2');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'التوصيات والإجراءات المقترحة';
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '27AE60' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    let row = 4;
    
    // التوصيات الاستراتيجية
    const strategicRecommendations = this.generateStrategicRecommendations(analysisResults);
    worksheet.addRow(['التوصيات الاستراتيجية']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    for (let i = 0; i < strategicRecommendations.length; i++) {
      worksheet.addRow([`${i + 1}.`, strategicRecommendations[i]]);
      row++;
    }

    // خطة التحسين
    row += 2;
    const improvementPlan = this.generateImprovementPlan(analysisResults);
    worksheet.addRow(['خطة التحسين المقترحة']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const planHeaders = ['الأولوية', 'المجال', 'الإجراء المطلوب', 'الجهة المسؤولة', 'الإطار الزمني', 'مؤشرات النجاح'];
    worksheet.addRow(planHeaders);

    for (const plan of improvementPlan) {
      worksheet.addRow(plan);
    }

    this.formatTable(worksheet, row, row + improvementPlan.length + 1, 6, 'green');
  }

  private calculateKPIs(analysisResults: AnalysisResults): ExportMetrics {
    const totalQuestions = Object.values(analysisResults.by_axis)
      .reduce((sum, axis) => sum + Object.keys(axis.question_averages).length, 0);
    
    const totalWorkshops = Object.keys(analysisResults.by_workshop).length;
    
    // حساب معدل الاكتمال (افتراضي)
    const completionRate = 95.5; // يمكن حسابه من البيانات الفعلية
    
    // حساب مؤشر الموثوقية (كرونباخ ألفا)
    const reliabilityIndex = this.calculateCronbachAlpha(analysisResults);
    
    const improvementAreas = this.identifyOverallImprovementAreas(analysisResults);
    const strongPoints = this.identifyOverallStrengths(analysisResults);

    return {
      totalQuestions,
      totalWorkshops,
      completionRate,
      reliabilityIndex,
      improvementAreas,
      strongPoints
    };
  }

  private calculatePercentage(value: number, maxScale: number): string {
    return `${((value / maxScale) * 100).toFixed(1)}%`;
  }



  private getPerformanceLevel(average: number): string {
    if (average >= 4.5) return 'ممتاز';
    if (average >= 4.0) return 'جيد جداً';
    if (average >= 3.5) return 'جيد';
    if (average >= 3.0) return 'متوسط';
    if (average >= 2.5) return 'ضعيف';
    return 'ضعيف جداً';
  }

  private generateRecommendation(average: number): string {
    if (average >= 4.5) return 'الحفاظ على التميز';
    if (average >= 4.0) return 'تعزيز النقاط الإيجابية';
    if (average >= 3.5) return 'تحسينات طفيفة مطلوبة';
    if (average >= 3.0) return 'يحتاج تطوير واضح';
    if (average >= 2.5) return 'يحتاج تدخل عاجل';
    return 'يحتاج إعادة تصميم كاملة';
  }

  private calculateCronbachAlpha(analysisResults: AnalysisResults): number {
    // حساب مبسط لمؤشر الموثوقية
    const axisAverages = Object.values(analysisResults.by_axis).map(axis => axis.average);
    const mean = axisAverages.reduce((sum, avg) => sum + avg, 0) / axisAverages.length;
    const variance = axisAverages.reduce((sum, avg) => sum + Math.pow(avg - mean, 2), 0) / axisAverages.length;
    
    // صيغة مبسطة
    const k = axisAverages.length;
    const avgVariance = variance;
    const totalVariance = variance * k;
    
    return (k / (k - 1)) * (1 - (avgVariance / totalVariance));
  }

  private identifyStrengths(workshopData: any): string {
    const strengths = [];
    const axisEntries = Object.entries(workshopData.by_axis || {});
    
    const topAxis = axisEntries.sort(([,a], [,b]) => (b as any).average - (a as any).average)[0];
    if (topAxis && (topAxis[1] as any).average >= 4.0) {
      strengths.push(`قوي في ${topAxis[0]}`);
    }
    
    return strengths.join(', ') || 'يحتاج تحليل أعمق';
  }

  private identifyImprovementAreas(workshopData: any): string {
    const improvements = [];
    const axisEntries = Object.entries(workshopData.by_axis || {});
    
    const weakAxis = axisEntries.filter(([,data]) => (data as any).average < 3.5);
    improvements.push(...weakAxis.map(([name]) => name));
    
    return improvements.slice(0, 2).join(', ') || 'أداء جيد عموماً';
  }

  private identifyOverallImprovementAreas(analysisResults: AnalysisResults): string[] {
    return Object.entries(analysisResults.by_axis)
      .filter(([,data]) => data.average < 3.5)
      .map(([name]) => name)
      .slice(0, 3);
  }

  private identifyOverallStrengths(analysisResults: AnalysisResults): string[] {
    return Object.entries(analysisResults.by_axis)
      .filter(([,data]) => data.average >= 4.0)
      .map(([name]) => name)
      .slice(0, 3);
  }

  private generateStrategicRecommendations(analysisResults: AnalysisResults): string[] {
    const recommendations = [];
    const overallAverage = analysisResults.overall.average;
    
    if (overallAverage < 3.0) {
      recommendations.push('إعادة تقييم شاملة لمحتوى وطرق التدريب');
      recommendations.push('تطوير برنامج تحسين عاجل للمدربين');
      recommendations.push('مراجعة المنهجية والأدوات المستخدمة');
    } else if (overallAverage < 4.0) {
      recommendations.push('تطوير نقاط الضعف المحددة في التحليل');
      recommendations.push('تعزيز التفاعل والمشاركة في التدريب');
      recommendations.push('تحسين المواد التدريبية والأنشطة');
    } else {
      recommendations.push('الحفاظ على مستوى التميز الحالي');
      recommendations.push('مشاركة أفضل الممارسات مع فرق أخرى');
      recommendations.push('التطوير المستمر والابتكار في المحتوى');
    }
    
    return recommendations;
  }

  private generateImprovementPlan(analysisResults: AnalysisResults): string[][] {
    const plan = [];
    const weakAreas = Object.entries(analysisResults.by_axis)
      .filter(([,data]) => data.average < 3.5)
      .sort(([,a], [,b]) => a.average - b.average);

    for (let i = 0; i < Math.min(weakAreas.length, 3); i++) {
      const [axisName, data] = weakAreas[i];
      plan.push([
        (i + 1).toString(),
        axisName,
        this.getImprovementAction(data.average),
        'فريق التدريب',
        this.getTimeframe(data.average),
        'زيادة المتوسط بـ 0.5 نقطة'
      ]);
    }

    return plan;
  }

  private getImprovementAction(average: number): string {
    if (average < 2.5) return 'إعادة تصميم كاملة للمحتوى';
    if (average < 3.0) return 'تطوير جذري للطرق والأدوات';
    return 'تحسينات مستهدفة وتطوير المحتوى';
  }

  private getTimeframe(average: number): string {
    if (average < 2.5) return '3-4 أشهر';
    if (average < 3.0) return '2-3 أشهر';
    return '1-2 شهر';
  }

  private generateRealisticTarget(average: number): string {
    if (average >= 4.5) {
      return 'الحفاظ على التميز والوصول إلى 5.0';
    } else if (average >= 4.0) {
      return `رفع المتوسط إلى ${Math.min(5.0, average + 0.3).toFixed(1)}`;
    } else if (average >= 3.5) {
      return `رفع المتوسط إلى ${Math.min(5.0, average + 0.5).toFixed(1)}`;
    } else if (average >= 3.0) {
      return `رفع المتوسط إلى ${Math.min(5.0, average + 0.7).toFixed(1)}`;
    } else {
      return `رفع المتوسط إلى ${Math.min(5.0, average + 1.0).toFixed(1)}`;
    }
  }

  private getCompletionStatus(rate: number): string {
    if (rate >= 95) return 'ممتاز';
    if (rate >= 85) return 'جيد';
    if (rate >= 75) return 'مقبول';
    return 'يحتاج تحسين';
  }

  private getReliabilityStatus(index: number): string {
    if (index >= 0.9) return 'موثوقية عالية جداً';
    if (index >= 0.8) return 'موثوقية عالية';
    if (index >= 0.7) return 'موثوقية مقبولة';
    return 'يحتاج تحسين الموثوقية';
  }

  private getPerformanceColor(level: string): string {
    const colors = {
      'ممتاز': 'أخضر',
      'جيد جداً': 'أزرق',
      'جيد': 'أصفر',
      'متوسط': 'برتقالي',
      'ضعيف': 'أحمر'
    };
    return colors[level as keyof typeof colors] || 'رمادي';
  }

  private formatTable(
    worksheet: ExcelJS.Worksheet,
    startRow: number,
    endRow: number,
    columns: number,
    colorScheme: string
  ) {
    const colors = this.colorSchemes[colorScheme as keyof typeof this.colorSchemes];
    
    for (let row = startRow; row <= endRow; row++) {
      for (let col = 1; col <= columns; col++) {
        const cell = worksheet.getCell(row, col);
        
        if (row === startRow) {
          // Header formatting
          cell.font = { bold: true, color: { argb: 'FFFFFF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors[0].substring(1) } };
        } else {
          // Data formatting
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors[3].substring(1) } };
        }
        
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    }
  }

  private async addChartsToWorksheet(
    worksheet: ExcelJS.Worksheet,
    axisData: AxisData,
    options: ExportOptions
  ) {
    // سيتم تطبيق هذه الوظيفة لاحقاً مع مكتبة الرسوم البيانية
    // placeholder للمحافظة على هيكل الكود
  }

  private async createChart(
    worksheet: ExcelJS.Worksheet,
    analysisResults: AnalysisResults,
    chartType: string,
    row: number,
    options: ExportOptions
  ) {
    // سيتم تطبيق هذه الوظيفة لاحقاً مع مكتبة الرسوم البيانية
    // placeholder للمحافظة على هيكل الكود
  }

  private async addAdvancedStatistics(
    worksheet: ExcelJS.Worksheet,
    analysisResults: AnalysisResults,
    row: number,
    options: ExportOptions
  ) {
    worksheet.addRow(['الإحصائيات المتقدمة']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const stats = [
      ['الانحراف المعياري', this.calculateStandardDeviation(analysisResults).toFixed(3)],
      ['معامل الاختلاف', this.calculateCoefficientOfVariation(analysisResults).toFixed(3)],
      ['أعلى أداء', this.getHighestPerformance(analysisResults)],
      ['أقل أداء', this.getLowestPerformance(analysisResults)],
      ['المدى', this.getRange(analysisResults).toFixed(2)]
    ];

    worksheet.addRow(['الإحصائية', 'القيمة']);
    for (const stat of stats) {
      worksheet.addRow(stat);
    }

    this.formatTable(worksheet, row, row + stats.length + 1, 2, options.colorScheme);
  }

  private calculateStandardDeviation(analysisResults: AnalysisResults): number {
    const averages = Object.values(analysisResults.by_axis).map(axis => axis.average);
    const mean = averages.reduce((sum, avg) => sum + avg, 0) / averages.length;
    const variance = averages.reduce((sum, avg) => sum + Math.pow(avg - mean, 2), 0) / averages.length;
    return Math.sqrt(variance);
  }

  private calculateCoefficientOfVariation(analysisResults: AnalysisResults): number {
    const averages = Object.values(analysisResults.by_axis).map(axis => axis.average);
    const mean = averages.reduce((sum, avg) => sum + avg, 0) / averages.length;
    const standardDeviation = this.calculateStandardDeviation(analysisResults);
    return (standardDeviation / mean) * 100;
  }

  private getHighestPerformance(analysisResults: AnalysisResults): string {
    const highest = Object.entries(analysisResults.by_axis)
      .sort(([,a], [,b]) => b.average - a.average)[0];
    return `${highest[0]} (${highest[1].average.toFixed(2)})`;
  }

  private getLowestPerformance(analysisResults: AnalysisResults): string {
    const lowest = Object.entries(analysisResults.by_axis)
      .sort(([,a], [,b]) => a.average - b.average)[0];
    return `${lowest[0]} (${lowest[1].average.toFixed(2)})`;
  }

  private getRange(analysisResults: AnalysisResults): number {
    const averages = Object.values(analysisResults.by_axis).map(axis => axis.average);
    return Math.max(...averages) - Math.min(...averages);
  }

  private async createWorkshopSpecificReport(
    analysisResults: AnalysisResults,
    axes: Record<string, string[]>,
    options: ExportOptions
  ) {
    const workshopName = options.workshopName!;
    const workshopData = analysisResults.by_workshop[workshopName];
    
    if (!workshopData) {
      throw new Error(`لم يتم العثور على بيانات الورشة: ${workshopName}`);
    }

    // ملخص الورشة التنفيذي
    await this.createWorkshopExecutiveSummary(workshopData, workshopName, options);
    
    // تحليل مفصل للمحاور
    await this.createWorkshopAxisAnalysis(workshopData, axes, workshopName, options);
    
    // ورقة الرسوم البيانية مع رسوم حقيقية
    if (options.includeCharts) {
      await this.createWorkshopChartsSheet(workshopData, workshopName, options);
    }
    
    // التوصيات والخطة التحسين
    await this.createWorkshopRecommendations(workshopData, workshopName);
    
    // إحصائيات متقدمة للورشة
    await this.createWorkshopStatistics(workshopData, workshopName, options);
  }

  private async createWorkshopExecutiveSummary(
    workshopData: any,
    workshopName: string,
    options: ExportOptions
  ) {
    // تشخيص مباشر لبيانات الورشة
    console.log(`🔍 تشخيص بيانات الورشة "${workshopName}":`, {
      response_count: workshopData.response_count,
      response_count_type: typeof workshopData.response_count,
      overall_average: workshopData.overall_average,
      performance_level: workshopData.performance_level,
      by_axis_count: Object.keys(workshopData.by_axis || {}).length,
      full_data: workshopData
    });
    
    const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`ملخص ورشة ${workshopName}`));

    // العنوان الرئيسي
    worksheet.mergeCells('A1:H3');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `تقرير تحليل ورشة العمل: ${workshopName}`;
    titleCell.font = { bold: true, size: 18, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // معلومات الورشة
    let row = 5;
    worksheet.addRow(['معلومات الورشة']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const workshopInfo = [
      ['اسم الورشة', workshopName],
      ['عدد الاستجابات', (workshopData.response_count !== undefined && workshopData.response_count !== null) ? workshopData.response_count.toString() : '0'],
      ['المتوسط العام', workshopData.overall_average.toFixed(2)],
      ['النسبة المئوية', this.calculatePercentage(workshopData.overall_average, 5)],
      ['مستوى الأداء', workshopData.performance_level],
      ['عدد المحاور', Object.keys(workshopData.by_axis).length.toString()],
      ['تاريخ التقرير', new Date().toLocaleDateString('ar-SA')]
    ];

    worksheet.addRow(['البيان', 'القيمة']);
    for (const info of workshopInfo) {
      worksheet.addRow(info);
    }

    this.formatTable(worksheet, row, row + workshopInfo.length + 1, 2, options.colorScheme);

    // تحليل المحاور
    row += workshopInfo.length + 4;
    worksheet.addRow(['أداء المحاور']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const axisHeaders = ['المحور', 'المتوسط', 'النسبة المئوية', 'مستوى الأداء', 'الحالة'];
    worksheet.addRow(axisHeaders);
    
    const sortedAxes = Object.entries(workshopData.by_axis)
      .sort(([,a], [,b]) => (b as any).average - (a as any).average)
      .map(([name, data]: [string, any]) => [
        name,
        data.average.toFixed(2),
        this.calculatePercentage(data.average, 5),
        data.performance_level,
        this.getPerformanceEmoji(data.average)
      ]);

    for (const axisData of sortedAxes) {
      worksheet.addRow(axisData);
    }

    this.formatTable(worksheet, row, row + sortedAxes.length + 1, 5, options.colorScheme);
  }

  private async createWorkshopAxisAnalysis(
    workshopData: any,
    axes: Record<string, string[]>,
    workshopName: string,
    options: ExportOptions
  ) {
    for (const [axisName, axisData] of Object.entries(workshopData.by_axis)) {
      const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`${axisName} - ${workshopName}`));

      // عنوان المحور
      worksheet.mergeCells('A1:F2');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `تحليل محور: ${axisName} - ورشة ${workshopName}`;
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '34495E' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

      // ملخص المحور
      let row = 4;
      const summaryData = [
        ['المتوسط', (axisData as any).average.toFixed(2)],
        ['النسبة المئوية', this.calculatePercentage((axisData as any).average, 5)],
        ['مستوى الأداء', (axisData as any).performance_level],
        ['عدد الأسئلة', Object.keys((axisData as any).question_averages).length.toString()],
        ['نقاط القوة', this.identifyAxisStrengths(axisData as any)],
        ['مجالات التحسين', this.identifyAxisWeaknesses(axisData as any)]
      ];

      worksheet.addRow(['المؤشر', 'القيمة']);
      for (const summary of summaryData) {
        worksheet.addRow(summary);
      }

      this.formatTable(worksheet, row, row + summaryData.length + 1, 2, options.colorScheme);

      // تفاصيل الأسئلة
      row += summaryData.length + 4;
      worksheet.addRow(['تفاصيل الأسئلة والدرجات']);
      worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
      row++;

      const questionHeaders = ['رقم', 'السؤال', 'المتوسط', 'النسبة المئوية', 'التقييم', 'التوصية'];
      worksheet.addRow(questionHeaders);

      const sortedQuestions = Object.entries((axisData as any).question_averages)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .map(([question, average], index) => [
          (index + 1).toString(),
          question,
          (average as number).toFixed(2),
          this.calculatePercentage(average as number, 5),
          this.getPerformanceLevel(average as number),
          this.generateQuestionRecommendation(average as number)
        ]);

      for (const questionData of sortedQuestions) {
        worksheet.addRow(questionData);
      }

      this.formatTable(worksheet, row, row + sortedQuestions.length + 1, 6, options.colorScheme);
    }
  }

  private async createWorkshopChartsSheet(
    workshopData: any,
    workshopName: string,
    options: ExportOptions
  ) {
    const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`رسوم بيانية - ${workshopName}`));

    // عنوان الورقة
    worksheet.mergeCells('A1:H2');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `الرسوم البيانية لورشة: ${workshopName}`;
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '8E44AD' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    let currentRow = 4;

    // إنشاء جدول بيانات للرسوم البيانية
    const axisData = Object.entries(workshopData.by_axis).map(([name, data]: [string, any]) => ({
      axis: name,
      average: data.average,
      percentage: parseFloat(this.calculatePercentage(data.average, 5).replace('%', ''))
    }));

    // جدول بيانات المحاور
    worksheet.addRow(['المحور', 'المتوسط', 'النسبة المئوية']);
    for (const axis of axisData) {
      worksheet.addRow([axis.axis, axis.average, axis.percentage]);
    }

    // إضافة رسم بياني نصي
    currentRow += axisData.length + 5;
    await this.createTextBasedChart(worksheet, workshopData, currentRow);
  }

  private async createWorkshopRecommendations(workshopData: any, workshopName: string) {
    const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`توصيات - ${workshopName}`));

    // العنوان
    worksheet.mergeCells('A1:F2');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `التوصيات وخطة التحسين - ورشة ${workshopName}`;
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '27AE60' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    let row = 4;
    
    // تقييم عام للورشة
    const overallRecommendations = this.generateWorkshopRecommendations(workshopData);
    worksheet.addRow(['التقييم العام والتوصيات']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    for (let i = 0; i < overallRecommendations.length; i++) {
      worksheet.addRow([`${i + 1}.`, overallRecommendations[i]]);
      row++;
    }

    // خطة التحسين المخصصة
    row += 2;
    const improvementPlan = this.generateWorkshopImprovementPlan(workshopData);
    worksheet.addRow(['خطة التحسين المقترحة']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const planHeaders = ['الأولوية', 'المحور', 'الإجراء المطلوب', 'المسؤول', 'الإطار الزمني', 'مؤشر النجاح'];
    worksheet.addRow(planHeaders);

    for (const plan of improvementPlan) {
      worksheet.addRow(plan);
    }

    this.formatTable(worksheet, row, row + improvementPlan.length + 1, 6, 'green');
  }

  private async createWorkshopStatistics(
    workshopData: any,
    workshopName: string,
    options: ExportOptions
  ) {
    const worksheet = this.workbook.addWorksheet(this.sanitizeWorksheetName(`إحصائيات - ${workshopName}`));

    // العنوان
    worksheet.mergeCells('A1:F2');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = `الإحصائيات المتقدمة - ورشة ${workshopName}`;
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3498DB' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    let row = 4;
    
    // إحصائيات أساسية
    const basicStats = this.calculateWorkshopStatistics(workshopData);
    worksheet.addRow(['الإحصائيات الأساسية']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const statsData = [
      ['المتوسط العام', basicStats.overallAverage.toFixed(2)],
      ['الانحراف المعياري', basicStats.standardDeviation.toFixed(3)],
      ['أعلى درجة', basicStats.maxScore.toFixed(2)],
      ['أقل درجة', basicStats.minScore.toFixed(2)],
      ['المدى', basicStats.range.toFixed(2)],
      ['معامل الاختلاف', `${basicStats.coefficientOfVariation.toFixed(1)}%`]
    ];

    worksheet.addRow(['الإحصائية', 'القيمة']);
    for (const stat of statsData) {
      worksheet.addRow(stat);
    }

    this.formatTable(worksheet, row, row + statsData.length + 1, 2, options.colorScheme);

    // تحليل التوزيع
    row += statsData.length + 4;
    worksheet.addRow(['تحليل توزيع الدرجات']);
    worksheet.getCell(`A${row}`).font = { bold: true, size: 14 };
    row++;

    const distribution = this.analyzeScoreDistribution(workshopData);
    const distributionData = [
      ['ممتاز (4.5-5.0)', distribution.excellent.toString()],
      ['جيد جداً (4.0-4.4)', distribution.veryGood.toString()],
      ['جيد (3.5-3.9)', distribution.good.toString()],
      ['متوسط (3.0-3.4)', distribution.average.toString()],
      ['ضعيف (أقل من 3.0)', distribution.poor.toString()]
    ];

    worksheet.addRow(['فئة الأداء', 'عدد المحاور']);
    for (const dist of distributionData) {
      worksheet.addRow(dist);
    }

    this.formatTable(worksheet, row, row + distributionData.length + 1, 2, options.colorScheme);
  }

  // دوال مساعدة جديدة
  private getPerformanceEmoji(average: number): string {
    if (average >= 4.5) return '🌟 ممتاز';
    if (average >= 4.0) return '✅ جيد جداً';
    if (average >= 3.5) return '👍 جيد';
    if (average >= 3.0) return '⚠️ متوسط';
    return '🔴 يحتاج تحسين';
  }

  private identifyAxisStrengths(axisData: any): string {
    const strongQuestions = Object.entries(axisData.question_averages)
      .filter(([, average]) => (average as number) >= 4.0)
      .length;
    
    if (strongQuestions > 0) {
      return `${strongQuestions} أسئلة قوية`;
    }
    return 'يحتاج تطوير';
  }

  private identifyAxisWeaknesses(axisData: any): string {
    const weakQuestions = Object.entries(axisData.question_averages)
      .filter(([, average]) => (average as number) < 3.5)
      .map(([question]) => question)
      .slice(0, 2);
    
    return weakQuestions.length > 0 ? weakQuestions.join(', ') : 'أداء جيد عموماً';
  }

  private generateQuestionRecommendation(average: number): string {
    if (average >= 4.5) return 'الحفاظ على التميز والاستمرار';
    if (average >= 4.0) return 'تعزيز وتطوير أكثر';
    if (average >= 3.5) return 'تحسينات بسيطة مطلوبة';
    if (average >= 3.0) return 'يحتاج تطوير واضح وسريع';
    return 'يحتاج إعادة نظر جذرية';
  }

  private generateWorkshopRecommendations(workshopData: any): string[] {
    const recommendations = [];
    const overallAverage = workshopData.overall_average;
    
    if (overallAverage >= 4.5) {
      recommendations.push('ورشة ممتازة - يمكن اعتبارها نموذجاً يحتذى به');
      recommendations.push('توثيق أفضل الممارسات ومشاركتها مع الورش الأخرى');
      recommendations.push('التركيز على التطوير المستمر والابتكار');
    } else if (overallAverage >= 4.0) {
      recommendations.push('ورشة جيدة جداً مع إمكانيات للوصول للتميز');
      recommendations.push('تطوير النقاط الأضعف للوصول للمستوى الممتاز');
      recommendations.push('تعزيز التفاعل والمشاركة أكثر');
    } else if (overallAverage >= 3.5) {
      recommendations.push('ورشة جيدة لكن تحتاج تحسينات واضحة');
      recommendations.push('مراجعة طرق التدريب والمحتوى المقدم');
      recommendations.push('زيادة التفاعل مع المتدربين');
    } else if (overallAverage >= 3.0) {
      recommendations.push('ورشة متوسطة تحتاج تطوير شامل');
      recommendations.push('إعادة تصميم المحتوى وطرق التقديم');
      recommendations.push('تدريب المحاضر على مهارات تدريبية أفضل');
    } else {
      recommendations.push('ورشة تحتاج تدخل عاجل وإعادة هيكلة كاملة');
      recommendations.push('مراجعة شاملة للمحتوى والمحاضر والبيئة التدريبية');
      recommendations.push('تطبيق خطة تحسين فورية وعاجلة');
    }
    
    return recommendations;
  }

  private generateWorkshopImprovementPlan(workshopData: any): string[][] {
    const plan = [];
    const axisEntries = Object.entries(workshopData.by_axis)
      .sort(([,a], [,b]) => (a as any).average - (b as any).average);

    for (let i = 0; i < Math.min(axisEntries.length, 3); i++) {
      const [axisName, data] = axisEntries[i];
      const average = (data as any).average;
      
      plan.push([
        (i + 1).toString(),
        axisName,
        this.getSpecificAction(average),
        'المحاضر المسؤول',
        this.getTimeframe(average),
        `${this.generateRealisticTarget(average)}`
      ]);
    }

    return plan;
  }

  private getSpecificAction(average: number): string {
    if (average < 2.5) return 'إعادة تصميم كاملة للمحتوى والطريقة';
    if (average < 3.0) return 'تطوير المحتوى وتحسين طرق التقديم';
    if (average < 3.5) return 'تحسين التفاعل وإضافة أنشطة عملية';
    if (average < 4.0) return 'تطوير المواد البصرية وزيادة المشاركة';
    return 'الحفاظ على المستوى وإضافة لمسات إبداعية';
  }

  private calculateWorkshopStatistics(workshopData: any) {
    const averages = Object.values(workshopData.by_axis).map((axis: any) => axis.average);
    const mean = averages.reduce((sum, avg) => sum + avg, 0) / averages.length;
    const variance = averages.reduce((sum, avg) => sum + Math.pow(avg - mean, 2), 0) / averages.length;
    const standardDeviation = Math.sqrt(variance);
    const maxScore = Math.max(...averages);
    const minScore = Math.min(...averages);
    const range = maxScore - minScore;
    const coefficientOfVariation = (standardDeviation / mean) * 100;

    return {
      overallAverage: mean,
      standardDeviation,
      maxScore,
      minScore,
      range,
      coefficientOfVariation
    };
  }

  private analyzeScoreDistribution(workshopData: any) {
    const averages = Object.values(workshopData.by_axis).map((axis: any) => axis.average);
    
    return {
      excellent: averages.filter(avg => avg >= 4.5).length,
      veryGood: averages.filter(avg => avg >= 4.0 && avg < 4.5).length,
      good: averages.filter(avg => avg >= 3.5 && avg < 4.0).length,
      average: averages.filter(avg => avg >= 3.0 && avg < 3.5).length,
      poor: averages.filter(avg => avg < 3.0).length
    };
  }

  private async createTextBasedChart(worksheet: ExcelJS.Worksheet, workshopData: any, startRow: number) {
    worksheet.addRow(['الرسم البياني للمحاور']);
    worksheet.getCell(`A${startRow}`).font = { bold: true, size: 14 };
    startRow++;

    // إنشاء رسم بياني نصي بسيط
    const axisEntries = Object.entries(workshopData.by_axis);
    
    worksheet.addRow(['المحور', 'المتوسط', 'المؤشر البصري']);
    startRow++;

    for (const [axisName, data] of axisEntries) {
      const average = (data as any).average;
      const barLength = Math.round((average / 5) * 20); // 20 حرف كحد أقصى
      const bar = '█'.repeat(barLength) + '░'.repeat(20 - barLength);
      const performance = this.getPerformanceEmoji(average);
      
      worksheet.addRow([
        axisName.substring(0, 25),
        average.toFixed(2),
        `${bar} ${performance}`
      ]);
    }

    this.formatTable(worksheet, startRow - 1, startRow + axisEntries.length, 3, 'blue');
  }

  private applyProfessionalFormatting(colorScheme: string) {
    // تطبيق تنسيق موحد على جميع الأوراق
    this.workbook.eachSheet((worksheet) => {
      worksheet.properties.defaultRowHeight = 20;
      worksheet.getColumn(1).width = 25;
      worksheet.getColumn(2).width = 15;
      worksheet.getColumn(3).width = 15;
      worksheet.getColumn(4).width = 20;
      worksheet.getColumn(5).width = 15;
      worksheet.getColumn(6).width = 30;
      worksheet.getColumn(7).width = 30;
    });
  }
}

// دالات مساعدة للتصدير السريع
export const exportToAdvancedExcel = async (
  analysisResults: AnalysisResults,
  axes: Record<string, string[]>,
  options: Partial<ExportOptions> = {}
): Promise<Buffer> => {
  const defaultOptions: ExportOptions = {
    includeCharts: true,
    includePercentages: true,
    includeDetailedAnalysis: true,
    chartTypes: ['column', 'pie'],
    colorScheme: 'professional'
  };

  const finalOptions = { ...defaultOptions, ...options };
  const exporter = new AdvancedExporter();
  return await exporter.generateAdvancedExcel(analysisResults, axes, finalOptions);
};

export const exportToPDF = async (
  analysisResults: AnalysisResults,
  axes: Record<string, string[]>
): Promise<Buffer> => {
  // سيتم تطبيق تصدير PDF لاحقاً
  throw new Error('PDF export not implemented yet');
};

export const exportToInteractiveDashboard = async (
  analysisResults: AnalysisResults,
  axes: Record<string, string[]>
): Promise<string> => {
  // سيتم تطبيق تصدير لوحة التحكم التفاعلية لاحقاً
  throw new Error('Interactive dashboard export not implemented yet');
}; 